import log from 'loglevel'
import {
  Datagrid,
  type FieldProps,
  type FilterPayload,
  Form,
  FunctionField,
  type Identifier,
  Labeled,
  ListBase,
  Loading,
  Pagination,
  type RaRecord,
  ReferenceField,
  SimpleShowLayout,
  type SortPayload,
  TextField,
  useGetList,
  useListContext,
  useNotify,
  usePermissions,
  useRecordContext,
  useTranslate,
  useUpdate
} from 'react-admin'
import { Box, Button, Card, CardContent, Grid, Typography } from '@mui/material'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { DateInput, TextInput, useScroll } from 'components/Util'
import React from 'react'
import { useFormContext, useFormState } from 'react-hook-form'
import {
  find,
  includes,
  isEmpty,
  isEqual,
  isNil,
  keys,
  startsWith
} from 'lodash/fp'
import {
  type Partner,
  type PartnerUser,
  type Product,
  type UserAttribute,
  UserStatus
} from '@mzc-watchover/adminapp-appsync'
import { assign } from 'lodash'
import { ScheduleButton } from 'components/Schedule'

log.setLevel(log.levels.DEBUG)

type SearchParams = {
  name: string
  address: string
  place: string
}

type ShowParams = {
  stop: string
  phone: string
  family: {
    name: string
    nameKana: string
    phone: string
    postalCode: string
  }
  schedules: string[]
}
type PartnerUserSub = Partial<PartnerUser> & {
  id: string
  contractor: { name?: string; address?: string }
}
const SearchButton: React.FC<{
  setFilter: (filter: FilterPayload) => void
  idLabel: string | undefined
}> = ({ setFilter, idLabel }) => {
  const [enable, setEnable] = React.useState(false)
  const { getValues } = useFormContext()
  const { errors, isValid, dirtyFields } = useFormState()
  const translate = useTranslate()
  const onSubmit = (): void => {
    const { ...data } = getValues() as SearchParams
    const filter = {}
    for (const field of keys(dirtyFields)) {
      assign(filter, {
        [field]:
          field !== 'attributes'
            ? data[field]
            : [{ key: idLabel, value: data[field] }]
      })
    }
    setFilter(filter)
  }
  React.useEffect(() => {
    setEnable(isValid)
  }, [errors, isValid])
  return (
    <Button
      variant="contained"
      color="primary"
      size="large"
      sx={{ borderRadius: '10px' }}
      fullWidth
      disabled={!enable}
      onClick={onSubmit}>
      <Typography variant="button">
        {translate('resources.users.edit.buttons.search')}
      </Typography>
    </Button>
  )
}
const SubmitButton: React.FC<{
  record: PartnerUserSub
  setRecord: (data: PartnerUserSub) => void
  schedules: string[] | null | undefined
}> = ({ record, setRecord, schedules }) => {
  const [enable, setEnable] = React.useState(false)
  const [data, setData] = React.useState({})
  const { getValues } = useFormContext()
  const translate = useTranslate()
  const { errors } = useFormState()
  const [update] = useUpdate(undefined, undefined, {
    onSettled: (data: PartnerUserSub | undefined) => {
      if (!isNil(data)) setRecord(data)
    }
  })
  const { stop, phone, family } = getValues() as ShowParams
  const {
    stop: currentStop,
    phone: currentPhone,
    families,
    start,
    status
  } = record
  const [currentSchedules, setCurrentSchedules] = React.useState<
    string[] | null | undefined
  >(record.schedules)
  const currentFamily =
    !isNil(families) && !isNil(families[0]) ? families[0] : undefined
  const onSubmit = (): void => {
    const { id } = record
    setEnable(false)
    // const { schedules } = getValues()
    // assign(data, { schedules })
    setCurrentSchedules(schedules)
    update('users', {
      id,
      data,
      previousData: record
    }).catch(log.error)
  }

  React.useEffect(() => {
    if (isEmpty(errors)) {
      const data = {}
      if (!isNil(stop) && stop !== currentStop)
        assign(data, {
          stop,
          status: isNil(start)
            ? status
            : !isNil(stop)
            ? UserStatus.stop
            : UserStatus.inUse
        })
      if (!isNil(phone) && phone !== currentPhone) {
        assign(data, { phone })
      }
      if (!isNil(currentFamily)) {
        const {
          name: currentFamilyName,
          nameKana: currentFamilyNameKana,
          phone: currentFamilyPhone,
          postalCode: currentFamilyPostalCode
        } = currentFamily
        const familyData = JSON.parse(JSON.stringify(currentFamily))
        if (!isNil(family.name) && family.name !== currentFamilyName) {
          assign(familyData, { name: family.name })
        }
        if (
          !isNil(family.nameKana) &&
          family.nameKana !== currentFamilyNameKana
        ) {
          assign(familyData, { nameKana: family.nameKana })
        }
        if (!isNil(family.phone) && family.phone !== currentFamilyPhone) {
          assign(familyData, { phone: family.phone })
        }
        if (
          !isNil(family.postalCode) &&
          family.postalCode !== currentFamilyPostalCode
        ) {
          assign(familyData, { postalCode: family.postalCode })
        }
        if (!isEmpty(familyData) && !isEqual(familyData)(currentFamily)) {
          assign(data, { families: [familyData] })
        }
      }
      // log.debug({
      //   schedules,
      //   currentSchedules,
      //   equal: isEqual(isNil(schedules) ? [] : schedules)(
      //     isNil(currentSchedules) ? [] : currentSchedules
      //   )
      // })
      if (
        !isEqual(isNil(schedules) ? [] : schedules)(
          isNil(currentSchedules) ? [] : currentSchedules
        )
      ) {
        assign(data, { schedules: isNil(schedules) ? [] : schedules })
      }
      // log.debug({ data })
      setEnable(!isEmpty(data))
      setData(data)
    }
  }, [
    errors,
    stop,
    phone,
    family.name,
    family.nameKana,
    family.phone,
    family.postalCode,
    schedules,
    record
  ])
  return (
    <Button
      variant="contained"
      color="primary"
      size="large"
      sx={{ borderRadius: '10px' }}
      fullWidth
      disabled={!enable}
      onClick={onSubmit}>
      <Typography variant="button">
        {translate('resources.users.show.buttons.submit')}
      </Typography>
    </Button>
  )
}
const UserEditForm: React.FC<{
  setFilter: (filter: FilterPayload) => void
  idLabel: string | undefined
  partner: Partial<Partner> | undefined
}> = ({ setFilter, idLabel, partner }) => {
  const translate = useTranslate()
  const validate = (values: any): Record<string, string> => {
    const { name, address, place } = values as SearchParams
    const errors: { children?: string } = {}
    if (isNil(name) && isNil(address) && isNil(place)) {
      errors.children = 'ra.validation.required'
    }
    return errors
  }
  if (isNil(partner)) return <></>
  const { products } = partner
  const solar = !isNil(
    find(
      (item: Product) =>
        !isNil(item.analytics) && startsWith('solar')(item.analytics)
    )(products)
  )
  return (
    <Form
      validate={validate}
      mode="onChange"
      reValidateMode="onChange"
      defaultValues={{
        name: null,
        address: null,
        place: null,
        attributes: null
      }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center'
        }}>
        <Box sx={{ width: '1280px' }}>
          <Box
            sx={{
              marginLeft: '0.5em',
              marginRight: '0.5em'
            }}>
            <Grid
              container
              justifyContent="center"
              spacing={2}
              sx={{
                marginTop: '0.1em'
              }}>
              <Grid item xs={12}>
                <Typography variant="h6">
                  {translate('resources.users.edit.title')}
                </Typography>
              </Grid>
              <Grid
                item
                xs={8}
                sx={{ marginTop: '0.25em', marginBottom: '0.25em' }}>
                <TextInput
                  source="name"
                  placeholder="中電太郎"
                  fields="resources.users.edit.fields"
                />
              </Grid>
              <Grid
                item
                xs={8}
                sx={{ marginTop: '0.25em', marginBottom: '0.25em' }}>
                <TextInput
                  source="address"
                  placeholder="愛知県名古屋市中区"
                  fields="resources.users.edit.fields"
                />
              </Grid>
              {!solar && (
                <Grid
                  item
                  xs={8}
                  sx={{ marginTop: '0.25em', marginBottom: '0.25em' }}>
                  <TextInput
                    source="place"
                    placeholder="KDX名古屋ビル"
                    fields="resources.users.edit.fields"
                  />
                </Grid>
              )}
              {!isNil(idLabel) && (
                <Grid
                  item
                  xs={8}
                  sx={{ marginTop: '0.25em', marginBottom: '0.25em' }}>
                  <TextInput
                    source="attributes"
                    placeholder="1234567890"
                    label={idLabel}
                  />
                </Grid>
              )}
              <Grid item xs={8} sx={{ marginTop: '0.5em' }}>
                <SearchButton setFilter={setFilter} idLabel={idLabel} />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </Form>
  )
}

const AttributeField: React.FC<FieldProps> = () => {
  const record = useRecordContext()
  const { attributes } = record
  if (isNil(attributes) || isNil(attributes[0])) return <></>
  const { key, value } = attributes[0]
  return <TextField record={{ [key]: value }} source={key} />
}
const UserListContent: React.FC<{
  setRecord: (data: PartnerUserSub) => void
  scrollRef: React.RefObject<HTMLDivElement>
  solar: boolean
}> = ({ setRecord, scrollRef, solar }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const { data, total, isLoading } = useListContext()
  React.useEffect(() => {
    if (isLoading) return
    if (total < 2) {
      setRecord(data[0])
      if (total === 0) {
        notify('resources.users.show.notifications.notFound')
      }
    }
  }, [isLoading, data, total])
  const select = (
    _id: Identifier,
    _resource: string,
    record: RaRecord
  ): string => {
    scrollRef?.current?.scrollIntoView()
    setRecord(record as PartnerUserSub)
    return 'toggleSelection'
  }
  const ref = useScroll(total)
  if (isLoading) return <Loading />
  if (total < 2 || isNil(data)) return <></>
  const { attributes } = data[0]
  let attributeKey
  if (!isNil(attributes) && !isNil(attributes[0])) {
    attributeKey = attributes[0].key
  }
  return (
    <Grid
      container
      justifyContent="center"
      spacing={2}
      sx={{ marginTop: '0.5em' }}>
      <Grid item xs={12}>
        <Typography variant="h6">
          {translate('resources.users.show.list.title')}
        </Typography>
        {!solar ? (
          <Datagrid
            rowClick={select}
            bulkActionButtons={false}
            sx={{ marginTop: '0.5em' }}>
            {!isNil(attributeKey) && <AttributeField source={attributeKey} />}
            <TextField source="name" />
            <TextField source="address" />
            <TextField source="place" />
            <TextField source="placeNumber" />
          </Datagrid>
        ) : (
          <Datagrid
            rowClick={select}
            bulkActionButtons={false}
            sx={{ marginTop: '0.5em' }}>
            {!isNil(attributeKey) && <AttributeField source={attributeKey} />}
            <TextField source="name" />
            <TextField source="address" />
          </Datagrid>
        )}
        {total > 10 && <Pagination />}
        <div ref={ref} />
      </Grid>
    </Grid>
  )
}
const UserList: React.FC<{
  filter: FilterPayload
  setRecord: (data: PartnerUserSub) => void
  scrollRef: React.RefObject<HTMLDivElement>
  solar: boolean
}> = ({ filter, setRecord, scrollRef, solar }) => {
  const sort: SortPayload = { field: 'name', order: 'ASC' }
  return (
    <ListBase resource="users" sort={sort} filter={filter}>
      <UserListContent
        setRecord={setRecord}
        scrollRef={scrollRef}
        solar={solar}
      />
    </ListBase>
  )
}
const UserTextField: React.FC<{
  source: string
  label: boolean
  ownLabel?: string
}> = ({ source, ownLabel }) => {
  const record = useRecordContext<PartnerUser>()
  const { start, analytics } = record
  return (
    <Labeled
      label={
        !isNil(ownLabel)
          ? ownLabel
          : isNil(analytics) || !startsWith('solar')(analytics)
          ? `resources.users.fields.${source}`
          : `resources.users.solarFields.${source}`
      }
      sx={{ fontVariant: 'body1' }}>
      {source === 'start' && (isNil(start) || isEmpty(start)) ? (
        <Typography variant="body1">利用開始前</Typography>
      ) : (
        <TextField source={source} sx={{ fontVariant: 'body1' }} />
      )}
    </Labeled>
  )
}
const UserReferenceField: React.FC<{
  source: string
  label: boolean
  ownLabel?: string
  reference: string
  children: JSX.Element
}> = ({ source, ownLabel, reference, children }) => {
  return (
    <Labeled
      label={!isNil(ownLabel) ? ownLabel : `resources.users.fields.${source}`}
      sx={{ fontVariant: 'body1' }}>
      <ReferenceField
        reference={reference}
        source={source}
        sx={{ fontVariant: 'body1' }}>
        {children}
      </ReferenceField>
    </Labeled>
  )
}
const UserRecordShow: React.FC<{
  record: PartnerUserSub
}> = ({ record }) => {
  const translate = useTranslate()
  const { stop, end, attributes, status, analytics, contractor } = record
  const idItem = find((item: UserAttribute) => item.key !== 'fileId')(
    attributes
  )
  const statusLabel = {
    [UserStatus.inUse]: '提供中',
    [UserStatus.switching]: '提供中',
    [UserStatus.switchingInWaitingResult]: '提供中',
    [UserStatus.switchingInWaitingStart]: '提供中',
    [UserStatus.beforeStart]: '提供準備中',
    [UserStatus.waitingResult]: '提供準備中',
    [UserStatus.waitingStart]: '提供準備中',
    [UserStatus.stop]: '解約予定',
    [UserStatus.end]: '解約済み'
  }
  const showRecord = {
    ...record,
    idItem: !isNil(idItem) ? idItem.value : undefined,
    status: statusLabel[status as keyof typeof statusLabel]
  }
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6">
          {translate('resources.users.show.record.title')}
        </Typography>
      </Grid>
      <Grid item xs={6}>
        <SimpleShowLayout record={showRecord}>
          <UserTextField source="name" label={false} />
          {!isNil(idItem) && (
            <UserTextField
              source="idItem"
              label={false}
              ownLabel={idItem.key}
            />
          )}
          <UserReferenceField
            reference="organizations"
            source="product"
            label={false}>
            <FunctionField
              source="contactOrganization"
              sx={{ fontVariant: 'body1' }}
              render={(record: Product) => {
                const { contactOrganization } = record
                return `${contactOrganization as string}さま`
              }}
            />
          </UserReferenceField>
          <UserTextField source="address" label={false} />
          {(isNil(analytics) || !startsWith('solar')(analytics)) && (
            <Grid container>
              <Grid item xs={8}>
                <UserTextField source="place" label={false} />
              </Grid>
              <Grid item xs={4}>
                <UserTextField source="placeNumber" label={false} />
              </Grid>
            </Grid>
          )}
          <UserTextField source="phone" label={false} />
          {!isNil(contractor) && !isEmpty(contractor.name) && (
            <Grid container>
              <Grid item xs={12}>
                <UserTextField source="contractor.name" label={false} />
              </Grid>
              <Grid item xs={12}>
                <UserTextField source="contractor.address" label={false} />
              </Grid>
            </Grid>
          )}
          {!isNil(analytics) && startsWith('solar')(analytics) && (
            <Grid container>
              <Grid item xs={12}>
                <UserTextField source="spin" label={false} />
              </Grid>
              <Grid item xs={12}>
                <UserTextField source="classification" label={false} />
              </Grid>
            </Grid>
          )}
        </SimpleShowLayout>
      </Grid>
      {(isNil(analytics) || !startsWith('solar')(analytics)) && (
        <Grid item xs={4}>
          <SimpleShowLayout record={showRecord}>
            <UserTextField source="families.0.name" label={false} />
            <UserTextField source="families.0.nameKana" label={false} />
            <UserTextField source="families.0.phone" label={false} />
            <UserTextField source="families.0.postalCode" label={false} />
          </SimpleShowLayout>
        </Grid>
      )}
      <Grid item xs={2}>
        <SimpleShowLayout record={showRecord}>
          <UserTextField source="status" label={false} />
          <UserTextField source="start" label={false} />
          {!isNil(stop) && !isEmpty(stop) && (
            <UserTextField source="stop" label={false} />
          )}
          {!isNil(end) && !isEmpty(end) && (
            <UserTextField source="end" label={false} />
          )}
        </SimpleShowLayout>
      </Grid>
    </Grid>
  )
}
const UserShowForm: React.FC<{
  filter: FilterPayload
  partner: Partial<Partner> | undefined
}> = ({ filter, partner }) => {
  const { permissions, isLoading: isLoadingPermissions } = usePermissions()
  const [record, setRecord] = React.useState<PartnerUserSub | null>(null)
  const [schedules, setSchedules] = React.useState<string[] | null | undefined>(
    record?.schedules
  )
  const family =
    !isNil(record) && !isNil(record.families) ? record.families[0] : undefined
  const schema = z.object({
    stop: z.string().nullable(),
    phone: z
      .string()
      .min(11, { message: 'too small' })
      .max(11, { message: 'too large' }),
    family: z.object({
      name: z.string().min(1),
      nameKana: z.string().min(1),
      phone: z
        .string()
        .min(11, { message: 'too small' })
        .max(11, { message: 'too large' }),
      postalCode: z
        .string()
        .min(7, { message: 'too small' })
        .max(7, { message: 'too large' })
    }),
    schedules: z.array(z.string()).optional()
  })
  const ref = useScroll(record)
  const { analytics, status } = !isNil(record)
    ? record
    : { analytics: undefined, status: undefined }
  React.useEffect(() => {
    if (!isNil(record)) setSchedules(record.schedules)
  }, [record])
  if (isNil(partner)) return <></>
  const { products } = partner
  const solar = !isNil(
    find(
      (item: Product) =>
        !isNil(item.analytics) && startsWith('solar')(item.analytics)
    )(products)
  )
  if (isLoadingPermissions) return <Loading />
  return (
    <Form
      resolver={zodResolver(schema)}
      mode="onChange"
      reValidateMode="onChange"
      defaultValues={
        isNil(record) || isNil(family)
          ? {}
          : {
              stop: isEmpty(record.stop) ? null : record.stop,
              phone: record.phone,
              family,
              schedules
            }
      }>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: '1280px' }}>
          <Box sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
            <UserList
              filter={filter}
              setRecord={setRecord}
              scrollRef={ref}
              solar={solar}
            />
          </Box>
          {!isNil(record) && (
            <>
              <Box sx={{ marginTop: '1em' }} id="user-show-content">
                <Box sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
                  <Grid
                    container
                    justifyContent="center"
                    alignItems="center"
                    spacing={2}
                    sx={{ marginTop: '0.1em' }}>
                    <Grid item xs={12}>
                      {!isNil(record) ? (
                        <UserRecordShow record={record} />
                      ) : (
                        <></>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              </Box>
              <Box sx={{ marginTop: '1em' }}>
                <Box sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
                  <Grid
                    container
                    justifyContent="center"
                    alignItems="center"
                    spacing={2}
                    sx={{ marginTop: '0.1em' }}>
                    <Grid
                      item
                      xs={4}
                      sx={{
                        marginBottom: '0.2em'
                      }}>
                      <DateInput
                        source="stop"
                        fields="resources.users.show.fields"
                        disabled={
                          includes('noStop')(permissions) ||
                          status === UserStatus.end
                        }
                      />
                    </Grid>
                  </Grid>
                  {(isNil(analytics) || !startsWith('solar')(analytics)) && (
                    <>
                      <Grid
                        container
                        justifyContent="center"
                        alignItems="center"
                        spacing={2}
                        sx={{ marginTop: '0.1em' }}>
                        <Grid
                          item
                          xs={4}
                          sx={{
                            marginBottom: '0.2em'
                          }}>
                          <TextInput
                            source="phone"
                            placeholder="09012345678"
                            fields="resources.users.show.fields"
                          />
                        </Grid>
                      </Grid>
                      <Grid
                        container
                        justifyContent="center"
                        alignItems="center"
                        spacing={2}
                        sx={{ marginTop: '0.1em' }}>
                        <Grid
                          item
                          xs={4}
                          sx={{
                            marginBottom: '0.2em'
                          }}>
                          <TextInput
                            source="family.name"
                            placeholder="中電太郎"
                            fields="resources.users.show.fields"
                          />
                        </Grid>
                      </Grid>
                      <Grid
                        container
                        justifyContent="center"
                        alignItems="center"
                        spacing={2}
                        sx={{ marginTop: '0.1em' }}>
                        <Grid
                          item
                          xs={4}
                          sx={{
                            marginBottom: '0.2em'
                          }}>
                          <TextInput
                            source="family.nameKana"
                            placeholder="チュウデンタロウ"
                            fields="resources.users.show.fields"
                          />
                        </Grid>
                      </Grid>
                      <Grid
                        container
                        justifyContent="center"
                        alignItems="center"
                        spacing={2}
                        sx={{ marginTop: '0.1em' }}>
                        <Grid
                          item
                          xs={4}
                          sx={{
                            marginBottom: '0.2em'
                          }}>
                          <TextInput
                            source="family.phone"
                            placeholder="09012345678"
                            fields="resources.users.show.fields"
                          />
                        </Grid>
                      </Grid>
                      {!isNil(family) && !isNil(family.postalCode) && (
                        <Grid
                          container
                          justifyContent="center"
                          alignItems="center"
                          spacing={2}
                          sx={{ marginTop: '0.1em' }}>
                          <Grid
                            item
                            xs={4}
                            sx={{
                              marginBottom: '0.2em'
                            }}>
                            <TextInput
                              source="family.postalCode"
                              placeholder="0123456"
                              fields="resources.users.show.fields"
                            />
                          </Grid>
                        </Grid>
                      )}
                      <Grid
                        container
                        justifyContent="center"
                        alignItems="center"
                        spacing={2}
                        sx={{ marginTop: '0.1em' }}>
                        <Grid
                          item
                          xs={4}
                          sx={{
                            marginBottom: '0.2em'
                          }}>
                          <ScheduleButton
                            source="schedules"
                            label="resources.users.show.fields.schedules"
                            setSchedules={setSchedules}
                          />
                        </Grid>
                      </Grid>
                    </>
                  )}
                </Box>
              </Box>
              <Box sx={{ marginTop: '1em' }}>
                <Box sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
                  <Grid
                    container
                    justifyContent="center"
                    alignItems="center"
                    spacing={2}
                    sx={{ marginTop: '0.1em' }}>
                    <Grid item xs={2}>
                      <SubmitButton
                        record={record}
                        setRecord={setRecord}
                        schedules={schedules}
                      />
                    </Grid>
                    <div ref={ref} />
                  </Grid>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Form>
  )
}
export const UserEdit: React.FC<{ partner: Partial<Partner> | undefined }> = ({
  partner,
  ...props
}) => {
  const [filter, setFilter] = React.useState<FilterPayload | undefined>(
    undefined
  )
  const [idLabel, setIdLabel] = React.useState<string | undefined>(undefined)
  const { data, isLoading } = useGetList<PartnerUser>('users', {
    pagination: { page: 1, perPage: 1 }
  })
  React.useEffect(() => {
    if (isLoading) return
    if (!isNil(data) && !isEmpty(data) && !isNil(data[0])) {
      const { attributes } = data[0]
      const idItem = find((item: UserAttribute) => item.key !== 'fileId')(
        attributes
      )
      if (!isNil(idItem)) {
        setIdLabel(idItem.key)
      }
    }
  }, [isLoading])
  if (isLoading) return <Loading />
  return (
    <>
      <Card raised sx={{ width: '1280px', marginBottom: '1em' }}>
        <CardContent>
          <UserEditForm
            {...props}
            setFilter={setFilter}
            idLabel={idLabel}
            partner={partner}
          />
        </CardContent>
      </Card>
      {!isNil(filter) && (
        <Card
          raised
          sx={{
            width: '1280px',
            marginBottom: '1em'
          }}>
          <CardContent>
            <UserShowForm {...props} filter={filter} partner={partner} />
          </CardContent>
        </Card>
      )}
    </>
  )
}
